/**
 * GenerateInvoiceButton Component - T168
 *
 * Button to generate an invoice from a contract
 */
import { useNavigate } from '@tanstack/react-router';
import { motion } from 'framer-motion';
import { useGenerateInvoice, type Contract } from '../../hooks/useContracts';
import { useToastHelpers } from '../ui';

interface GenerateInvoiceButtonProps {
  contract: Contract;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

export function GenerateInvoiceButton({ contract, size = 'md', className = '' }: GenerateInvoiceButtonProps) {
  const navigate = useNavigate();
  const generateInvoice = useGenerateInvoice();
  const toast = useToastHelpers();

  const isActive = contract.status === 'active';
  const hasItems = contract.items && contract.items.length > 0;
  const canGenerate = isActive && hasItems;

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-5 py-2.5 text-base',
  };

  const handleClick = async () => {
    if (!canGenerate) return;

    try {
      const invoice = await generateInvoice.mutateAsync(contract.id);
      toast.success('Rechnung erfolgreich erstellt');
      navigate({ to: '/invoices/$id', params: { id: invoice.id } });
    } catch {
      toast.error('Fehler beim Erstellen der Rechnung');
    }
  };

  if (!canGenerate) {
    return null;
  }

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={handleClick}
      disabled={generateInvoice.isPending}
      className={`
        inline-flex items-center gap-2 font-medium rounded-lg
        bg-blue-600 hover:bg-blue-500 text-white
        disabled:opacity-50 disabled:cursor-not-allowed
        transition-colors
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {generateInvoice.isPending ? (
        <>
          <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Erstelle...</span>
        </>
      ) : (
        <>
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          <span>Rechnung erstellen</span>
        </>
      )}
    </motion.button>
  );
}
